import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { IMaskInput } from 'react-imask'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { apiFetch } from '../../lib/api'
import { useAuth } from '../../state/auth'
import { maskCnpj } from '../../lib/format'
import { ConfirmModal } from '../ConfirmModal'

type Company = {
  id: string
  legalName: string
  tradeName: string
  cnpjMasked: string
  cnpjDigits?: string
  cnae: string | null
  taxRegime: 'simples' | 'presumido' | 'real'
  city: string | null
  state: string | null
  isActive: boolean
}

const BRAZILIAN_STATES = [
  { value: '', label: 'Selecione' },
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' }
]

const schema = z.object({
  legalName: z.string().min(2, 'Informe a razão social'),
  tradeName: z.string().min(2, 'Informe o nome fantasia'),
  cnpj: z.string().min(18, 'Informe o CNPJ completo'),
  cnae: z.string().optional(),
  taxRegime: z.enum(['simples', 'presumido', 'real']),
  city: z.string().optional(),
  state: z.string().optional()
})

type FormData = z.input<typeof schema>

export function CompaniesPage() {
  const { me, refreshMe, setSelectedCompanyId } = useAuth()
  const memberships = me?.memberships ?? []
  const canCreate = memberships.length === 0 || memberships.some((m) => m.role === 'admin')
  const qc = useQueryClient()
  const [filter, setFilter] = useState('')
  const [editingCompany, setEditingCompany] = useState<Company | null>(null)
  const [deleteCompanyConfirm, setDeleteCompanyConfirm] = useState<Company | null>(null)

  const companiesQ = useQuery({
    queryKey: ['companies'],
    queryFn: () => apiFetch<{ companies: Company[] }>('/companies')
  })

  const companies = companiesQ.data?.companies ?? []
  const filteredCompanies = useMemo(() => {
    const f = filter.trim().toLowerCase()
    if (!f) return companies
    return companies.filter(
      (c) =>
        c.tradeName.toLowerCase().includes(f) ||
        c.legalName.toLowerCase().includes(f) ||
        (c.cnpjMasked && c.cnpjMasked.replace(/\D/g, '').includes(f.replace(/\D/g, '')))
    )
  }, [companies, filter])

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { taxRegime: 'simples', city: '', state: '' }
  })

  useEffect(() => {
    if (editingCompany) {
      reset({
        legalName: editingCompany.legalName,
        tradeName: editingCompany.tradeName,
        cnpj: editingCompany.cnpjMasked ?? maskCnpj(editingCompany.cnpjDigits ?? ''),
        cnae: editingCompany.cnae ?? '',
        taxRegime: editingCompany.taxRegime,
        city: editingCompany.city ?? '',
        state: editingCompany.state ?? ''
      })
    }
  }, [editingCompany, reset])

  const onSubmit = async (data: FormData) => {
    if (editingCompany) {
      await apiFetch(`/companies/${editingCompany.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          legalName: data.legalName,
          tradeName: data.tradeName,
          cnpj: data.cnpj,
          cnae: data.cnae || null,
          taxRegime: data.taxRegime,
          city: data.city || null,
          state: data.state || null
        })
      })
      toast.success('Empresa atualizada')
      setEditingCompany(null)
    } else {
      const created = await apiFetch<{ ok: true; id: string }>('/companies', {
        method: 'POST',
        body: JSON.stringify({
          legalName: data.legalName,
          tradeName: data.tradeName,
          cnpj: data.cnpj,
          cnae: data.cnae || null,
          taxRegime: data.taxRegime,
          city: data.city || null,
          state: data.state || null
        })
      })
      toast.success('Empresa criada')
      await refreshMe()
      setSelectedCompanyId(created.id)
    }
    await qc.invalidateQueries({ queryKey: ['companies'] })
    reset({ legalName: '', tradeName: '', cnpj: '', cnae: '', taxRegime: 'simples', city: '', state: '' })
  }

  const openDeleteCompanyConfirm = (c: Company) => setDeleteCompanyConfirm(c)

  const handleDelete = async () => {
    if (!deleteCompanyConfirm) return
    const c = deleteCompanyConfirm
    setDeleteCompanyConfirm(null)
    await apiFetch(`/companies/${c.id}`, { method: 'DELETE' })
    toast.success('Empresa excluída')
    await refreshMe()
    await qc.invalidateQueries({ queryKey: ['companies'] })
  }

  return (
    <div className="space-y-6">
      <ConfirmModal
        open={!!deleteCompanyConfirm}
        title="Excluir empresa"
        message={
          deleteCompanyConfirm
            ? `Excluir a empresa "${deleteCompanyConfirm.tradeName}"? Ela será marcada como inativa.`
            : ''
        }
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteCompanyConfirm(null)}
      />
      <div>
        <div className="text-xl font-semibold text-slate-900">Empresas</div>
        <div className="mt-1 text-sm text-slate-500">
          Cadastro de empresas para organização e cálculo de rescisão.
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-semibold text-slate-900">Lista</div>
            <input
              type="text"
              placeholder="Buscar empresa..."
              className="h-9 w-full max-w-xs rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="mt-4 space-y-2">
            {filteredCompanies.map((c) => (
              <div key={c.id} className="rounded-xl border border-slate-200 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900">{c.tradeName}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {c.legalName} • {c.cnpjMasked}
                    </div>
                    {(c.city || c.state) && (
                      <div className="mt-1 text-xs text-slate-500">
                        {[c.city, c.state].filter(Boolean).join(' • ')}
                      </div>
                    )}
                    <div className="mt-2 text-xs text-slate-600">
                      Regime: {c.taxRegime.toUpperCase()}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <span
                      className={[
                        'rounded-full px-3 py-1 text-xs font-semibold',
                        c.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
                      ].join(' ')}
                    >
                      {c.isActive ? 'Ativa' : 'Inativa'}
                    </span>
                    {canCreate && (
                      <>
                        <button
                          type="button"
                          className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200"
                          onClick={() => setEditingCompany(c)}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
                          onClick={() => openDeleteCompanyConfirm(c)}
                        >
                          Excluir
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {companiesQ.isLoading && <div className="text-sm text-slate-500">Carregando…</div>}
            {companiesQ.isError && (
              <div className="text-sm text-red-600">Falha ao carregar empresas.</div>
            )}
            {!companiesQ.isLoading && !companiesQ.isError && filteredCompanies.length === 0 && (
              <div className="text-sm text-slate-500">
                {filter ? 'Nenhuma empresa encontrada com o filtro.' : 'Nenhuma empresa cadastrada.'}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-sm font-semibold text-slate-900">
            {editingCompany ? 'Editar empresa' : 'Cadastrar empresa'}
          </div>
          {editingCompany && (
            <button
              type="button"
              className="mt-2 text-xs text-slate-500 underline"
              onClick={() => {
                setEditingCompany(null)
                reset({ legalName: '', tradeName: '', cnpj: '', cnae: '', taxRegime: 'simples', city: '', state: '' })
              }}
            >
              Cancelar edição
            </button>
          )}
          {!canCreate && !editingCompany ? (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              Você não tem permissão de administrador para cadastrar empresas.
            </div>
          ) : (
            <form className="mt-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-slate-700">Razão Social</label>
                  <input
                    className="mt-1 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900"
                    {...register('legalName')}
                  />
                  {errors.legalName && (
                    <div className="mt-1 text-xs text-red-600">{errors.legalName.message}</div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="text-sm font-medium text-slate-700">Nome Fantasia</label>
                  <input
                    className="mt-1 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900"
                    {...register('tradeName')}
                  />
                  {errors.tradeName && (
                    <div className="mt-1 text-xs text-red-600">{errors.tradeName.message}</div>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">CNPJ</label>
                  <IMaskInput
                    mask="00.000.000/0000-00"
                    value={watch('cnpj') ?? ''}
                    className="mt-1 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900"
                    onAccept={(v) => setValue('cnpj', String(v))}
                  />
                  {errors.cnpj && (
                    <div className="mt-1 text-xs text-red-600">{errors.cnpj.message}</div>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">CNAE</label>
                  <input
                    className="mt-1 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900"
                    {...register('cnae')}
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Regime Tributário</label>
                  <select
                    className="mt-1 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900"
                    {...register('taxRegime')}
                  >
                    <option value="simples">Simples</option>
                    <option value="presumido">Presumido</option>
                    <option value="real">Real</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Cidade</label>
                  <input
                    className="mt-1 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900"
                    {...register('city')}
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Estado</label>
                  <select
                    className="mt-1 h-11 w-full rounded-xl border border-slate-200 bg-white px-4 text-sm text-slate-900"
                    {...register('state')}
                  >
                    {BRAZILIAN_STATES.map((s) => (
                      <option key={s.value || 'empty'} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                disabled={isSubmitting}
                className="h-11 w-full rounded-xl bg-slate-900 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                type="submit"
              >
                {isSubmitting ? 'Salvando…' : editingCompany ? 'Atualizar' : 'Salvar'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
